import { memo, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, CircularProgress, CardActions, styled, Chip } from '@mui/material'
import CardBase from 'features/common/ui/Cards/CardBase/CardBase'
import WorkflowCardHeader from './WorkflowCardHeader'
import WorkflowCardBadges from './WorkflowCardBadges'
import WorkflowThumbnail from './WorkflowThumbnail'
import PublishDate from 'features/common/ui/Cards/PublishDate'
import CardOwnerAvatar from 'features/common/ui/Cards/CardOwnerAvatar'
import { ExtendedWorkflow, WorkflowDemo, WorkflowListItem } from 'features/workflows/types'
import { Typography } from '@carto/meridian-ds/components'
import { targetOrParentHasClass } from 'features/common/utils/dom'
import TagsCardFooter from 'features/tags/ui/TagsCardFooter'

const CardBaseContainer = styled(CardBase)(() => ({
  position: 'relative'
}))

const Loader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.white[90],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10
}))

const Actions = styled(CardActions)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 1.5)
}))

interface WorkflowCardProps {
  workflow: WorkflowListItem | ExtendedWorkflow
  loading?: boolean
  demo?: WorkflowDemo
  loadingMessage?: ReactNode
  onClick?: () => void
}

function WorkflowCard({ workflow, onClick, demo, loading, loadingMessage }: WorkflowCardProps) {
  const intl = useIntl()
  const { updatedAt, ownerPicture: ownerPictureUrl, ownerEmail, ownerIsCartoUser, tags } = workflow
  const { title } = workflow?.config || { title: '' }
  const isDemo = Boolean(demo) ?? false
  const exists = demo?.exists
  const workflowTitle = title || intl.formatMessage({ id: 'workflows.default.title' })

  const handleClick = (ev) => {
    if (targetOrParentHasClass(ev.target, 'MuiButtonBase-root')) return

    onClick?.()
  }

  return (
    <CardBaseContainer onClick={handleClick} aria-label={workflowTitle}>
      {loading && (
        <Loader>
          <CircularProgress />
          <Typography mt={2} variant="caption">
            {loadingMessage}
          </Typography>
        </Loader>
      )}
      <Box aria-label="workflow-card">
        <WorkflowCardBadges workflow={workflow} />
        <WorkflowThumbnail title={workflowTitle} url={workflow.thumbnailUrl} />
        <WorkflowCardHeader workflow={workflow} demo={demo} />
        <Actions>
          <PublishDate
            date={updatedAt}
            ownerAvatar={
              !isDemo || exists ? (
                <CardOwnerAvatar picture={ownerPictureUrl} email={ownerEmail} cartoUser={ownerIsCartoUser} />
              ) : null
            }
          />
          {isDemo && (
            <Chip
              label={
                <Typography component="p" variant="caption" noWrap>
                  <FormattedMessage id="workflows.demo.tag" />
                </Typography>
              }
              size="small"
              variant="outlined"
              color="default"
            />
          )}
          <TagsCardFooter tags={tags} />
        </Actions>
      </Box>
    </CardBaseContainer>
  )
}

export default memo(WorkflowCard)
